import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, TextField, Paper,
} from '@mui/material';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import CloudDoneOutlinedIcon from '@mui/icons-material/CloudDoneOutlined';
import CloudSyncOutlinedIcon from '@mui/icons-material/CloudSyncOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-body)', fontSize: '0.88rem',
    backgroundColor: 'var(--bg-subtle)', transition: 'all 0.2s ease',
    '& fieldset': { border: '2px solid transparent' },
    '&:hover fieldset': { border: '2px solid var(--border-hover)' },
    '&.Mui-focused fieldset': { border: '2px solid var(--accent)' },
    '&.Mui-focused': { backgroundColor: 'var(--bg-card)' },
  },
};

const statusStyles = {
  typing: { icon: EditNoteOutlinedIcon, label: 'Escribiendo...', bg: 'var(--bg-subtle)', border: 'var(--border)', color: 'var(--text-muted)' },
  saving: { icon: CloudSyncOutlinedIcon, label: 'Guardando...', bg: 'var(--warning-bg)', border: 'var(--warning)', color: 'var(--warning)', spin: true },
  saved: { icon: CloudDoneOutlinedIcon, label: 'Guardado', bg: 'var(--success-bg)', border: 'var(--success)', color: 'var(--success)' },
  added: { icon: CloudDoneOutlinedIcon, label: 'Observación agregada', bg: '#eff6ff', border: '#3b82f6', color: '#3b82f6' },
  modified: { icon: CloudDoneOutlinedIcon, label: 'Observación modificada', bg: '#faf5ff', border: '#7c3aed', color: '#7c3aed' },
  deleted: { icon: DeleteOutlineIcon, label: 'Observación eliminada', bg: 'var(--danger-bg)', border: 'var(--danger)', color: 'var(--danger)' },
  error: { icon: CloudSyncOutlinedIcon, label: 'Error al guardar', bg: 'var(--danger-bg)', border: 'var(--danger)', color: 'var(--danger)' },
};

const SeccionObservacionGeneral = ({ observacion = '', onSave }) => {
  const [text, setText] = useState(String(observacion || ''));
  const [savedText, setSavedText] = useState(String(observacion || ''));
  const [status, setStatus] = useState(observacion ? 'saved' : 'idle');
  const timerRef = useRef(null);

  useEffect(() => {
    const obs = String(observacion || '');
    setText(obs);
    setSavedText(obs);
    if (obs) setStatus('saved');
  }, [observacion]);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const handleChange = (e) => {
    if (e.target.value.length > 2000) return;
    const val = e.target.value;
    setText(val);
    setStatus('typing');

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => autoSave(val), 800);
  };

  const autoSave = (val) => {
    setStatus('saving');
    try {
      if (onSave) onSave(val);
      const prev = savedText;
      setSavedText(val);
      if (val === '') setStatus('deleted');
      else if (prev === '') setStatus('added');
      else setStatus('modified');
      setTimeout(() => setStatus(val ? 'saved' : 'idle'), 2500);
    } catch (e) {
      console.error('Error guardando observación general:', e);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  const st = statusStyles[status];

  return (
    <Paper elevation={0} sx={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', backgroundColor: 'var(--bg-card)', mb: 2, overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', backgroundColor: '#faf5ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <StickyNote2OutlinedIcon sx={{ color: '#7c3aed', fontSize: 20 }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: '0.95rem', color: 'var(--text-primary)' }}>
              Observación del Vendedor
            </Typography>
            <Typography sx={{ fontFamily: 'var(--font-body)', fontSize: '0.7rem', color: 'var(--text-muted)' }}>
              Nota interna visible solo para el vendedor
            </Typography>
          </Box>
        </Box>

        {/* Status pill */}
        {st && (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.5,
            px: 1.25, py: 0.35, borderRadius: 'var(--radius-full)',
            bgcolor: st.bg, border: `1px solid ${st.border}`, color: st.color,
            transition: 'all 0.3s ease',
          }}>
            <st.icon sx={{
              fontSize: 13,
              ...(st.spin && { animation: 'spin 1s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }),
            }} />
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, fontFamily: 'var(--font-body)' }}>
              {st.label}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Content */}
      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth multiline rows={3}
          placeholder="Escribí una observación general para este pedido..."
          value={text}
          onChange={handleChange}
          sx={{
            ...inputSx,
            '& .MuiOutlinedInput-root': {
              ...inputSx['& .MuiOutlinedInput-root'],
              '& fieldset': {
                border: status === 'saving' ? '2px solid var(--warning)' :
                  (status === 'added' || status === 'modified') ? '2px solid var(--success)' :
                    status === 'deleted' ? '2px solid var(--danger)' : '2px solid transparent',
                transition: 'border-color 0.3s ease',
              },
            },
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.75 }}>
          <Typography sx={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontFamily: 'var(--font-body)' }}>
            Se guarda automáticamente
          </Typography>
          <Typography sx={{
            fontSize: '0.68rem', fontFamily: 'var(--font-mono)',
            color: (text || '').length > 1800 ? 'var(--warning)' : 'var(--text-muted)',
            fontWeight: (text || '').length > 1800 ? 700 : 400,
          }}>
            {(text || '').length}/2000
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default SeccionObservacionGeneral;